import { ColorTypes } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { CustomerEntity } from '@genial/services/dist/models';
import { Utils, useBreakpointValue } from '@genial/utils';
import React, { useState } from 'react';

import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { getCustomerBalance } from '../../../../../BatchTicket/services';
import {
  errorBalanceUpdateMessage,
  successBalanceUpdateMessage,
} from '../../../../../BatchTicket/utils';
import { messageError } from '../../constants';

import { Desktop } from './desktop';
import { Mobile } from './mobile';
import { Tablet } from './tablet';
export interface CustomerLayoutProps {
  backgroundColorBadge: ColorTypes.Option;
  name: string;
  document: string;
  account: string;
  suitability: string;
  registrationStatus: string;
  balance: {
    available: number;
    isLoading: boolean;
    onUpdateButton: () => Promise<void>;
    estimated: string;
    lastUpdatedAt: Date;
  };
}

interface CustomerProps {
  data?: CustomerEntity;
  totalPrice: number;
}

export const Customer: React.FC<CustomerProps> = ({ data, totalPrice }) => {
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(data?.availableBalance || 0);

  const { showToast, showToastFromError } = useToastCreator('toast-balance-customer-equity-edit');

  const name = data?.name || messageError;
  const document = data?.cpfCnpjFormatted || messageError;
  const account = data?.accountNumber.toString() || messageError;
  const suitability = data?.suitabilityProfileName || messageError;
  const registrationStatus = data?.registrationStatusName || messageError;
  const backgroundColorBadge = `risk.${suitability.toLowerCase()}` as ColorTypes.Option;

  const handleUpdateBalance = async () => {
    try {
      setIsLoadingBalance(true);

      const availableBalanceResponse = await getCustomerBalance(data?.accountNumber!);

      if (availableBalanceResponse !== availableBalance) {
        setAvailableBalance(availableBalanceResponse);
      }

      showToast(...successBalanceUpdateMessage, { duration: 3000 });
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorBalanceUpdateMessage);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const balance = {
    available: availableBalance,
    isLoading: isLoadingBalance,
    onUpdateButton: handleUpdateBalance,
    estimated: Utils.Mask.Money(availableBalance - totalPrice),
    lastUpdatedAt: new Date(),
  };

  const props = {
    backgroundColorBadge,
    name,
    document,
    account,
    suitability,
    registrationStatus,
    balance,
    handleUpdateBalance,
  };

  const layout = useBreakpointValue({
    base: <Mobile {...props} />,
    sm: <Tablet {...props} />,
    lg: <Desktop {...props} />,
  });

  return layout;
};
